import { useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { track } from '@vercel/analytics'
import BetaForm from '../../components/marketing/BetaForm'
import { BetaSignupData, BetaError, LogoIcon } from '@talktoast/shared'

export default function BetaSignUpPage() {
  const navigate = useNavigate()

  useEffect(() => {
    // Track page view
    track('beta_signup_page_viewed')
  }, [])

  const handleSuccess = (data: BetaSignupData & { position?: number }) => {
    // Track successful signup
    track('beta_signup_completed', {
      email: data.email,
      referral_source: data.referralSource || 'not_specified',
      has_dietary_prefs: (data.dietaryPreferences?.length || 0) > 0,
      has_household_size: !!data.householdSize,
      has_grocery_store: !!data.primaryGroceryStore,
      position: data.position,
    })

    // Navigate to confirmation page with user data
    navigate('/beta-confirmation', {
      state: {
        name: data.name,
        email: data.email,
        position: data.position,
      },
    })
  }

  const handleError = (error: BetaError) => {
    // Track signup errors
    track('beta_signup_failed', {
      error_code: error.code,
      error_message: error.message,
    })

    // Log error for debugging
    console.error('Beta signup error:', error)
  }

  const handleFormStarted = () => {
    // Track when user starts filling the form
    track('beta_signup_started')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <LogoIcon size="sm" alt="TalkToast" />
              <h1 className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent">
                TalkToast
              </h1>
            </div>
            <a
              href="/"
              className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
            >
              Back to Home
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          {/* Hero Section */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-pink-500 rounded-full mb-4">
              <span className="text-3xl">🎉</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Join the Beta Waitlist
            </h2>
            <p className="text-lg text-gray-600 max-w-xl mx-auto">
              Be among the first to experience AI-powered meal planning that adapts to your lifestyle.
              Our beta launches in <strong>June 2026</strong>.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <div className="flex items-start space-x-3 p-4 bg-orange-50 rounded-lg">
              <span className="text-2xl">🤖</span>
              <div>
                <h3 className="font-semibold text-gray-900">AI-Powered Plans</h3>
                <p className="text-sm text-gray-600">
                  Personalized meal plans using Claude AI
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3 p-4 bg-pink-50 rounded-lg">
              <span className="text-2xl">🛒</span>
              <div>
                <h3 className="font-semibold text-gray-900">Smart Shopping</h3>
                <p className="text-sm text-gray-600">
                  One-click grocery ordering with Instacart
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3 p-4 bg-purple-50 rounded-lg">
              <span className="text-2xl">📅</span>
              <div>
                <h3 className="font-semibold text-gray-900">Calendar Sync</h3>
                <p className="text-sm text-gray-600">
                  Integrate meals with your daily schedule
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg">
              <span className="text-2xl">🎯</span>
              <div>
                <h3 className="font-semibold text-gray-900">Dietary Flexibility</h3>
                <p className="text-sm text-gray-600">
                  Supports all diets and restrictions
                </p>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 my-8"></div>

          {/* Form Section */}
          <div onFocus={handleFormStarted}>
            <BetaForm onSuccess={handleSuccess} onError={handleError} />
          </div>
        </div>

        {/* Social Proof */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            Join <strong className="text-gray-900">1,000+</strong> food lovers waiting for early access
          </p>
        </div>
      </main>
    </div>
  )
}
